import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { BackToListRow, Button, RobbieMark } from '../../../components/ui';
import { EndUserHandoffBanner, SubStepPills } from '../../../components/wizard';
import type { EndUserHandoffView } from '../../../hooks/useEndUserHandoff';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useLongWait } from '../../../hooks/useLongWait';
import { validationCopy } from '../../../utils/waitCopy';
import { QuestionsSkeleton } from '../context/AnalysisWait';
import { ValidationOverlay, ValidationStepBar } from './ValidationWait';
import ValidationDetailPane from './ValidationDetailPane';
import ValidationListPane, { ValidationListItem } from './ValidationListPane';
import { formatRuleName, severityLabel } from './format';
import { AISuggestion } from './types';
import { ValidationStepState } from './useValidationStep';
import { BOM_ENTRY_KEY } from './validationEntries';

/**
 * Step 3b — validation as the same master–detail shape as Discovery:
 * component list on the left (with live "N to fix" chips), the selected
 * component's findings and improvement ideas on the right. Fixes apply
 * instantly; re-validation only happens when the user explicitly asks
 * Robbie to re-check (the header shows how many changes a re-check would
 * fold in). The check itself runs under a Robbie overlay; the StepBar
 * leads on to Find suppliers.
 *
 * A viewer reads the findings and Robbie's ideas — no re-check, no fixes, and
 * an honest line where the owner would see the button to run the checks.
 */
export interface ValidationStepPanelProps {
  state: ValidationStepState;
  aiSuggestion: AISuggestion | null;
  onBack: () => void;
  onContinue: () => void;
  isContinueLoading?: boolean;
  onSubStepSelect?: (id: string) => void;
  /** End-user handoff view — drives the banner and swaps the footer CTA. */
  handoff?: EndUserHandoffView;
}

const PILLS = [
  { id: 'context', label: 'Context', state: 'done' as const },
  { id: 'validation', label: 'Validation', state: 'active' as const },
  { id: 'discovery', label: 'Discovery', state: 'upcoming' as const },
];

export const ValidationStepPanel: React.FC<ValidationStepPanelProps> = ({
  state,
  aiSuggestion,
  onBack,
  onContinue,
  isContinueLoading = false,
  onSubStepSelect,
  handoff,
}) => {
  // state.readOnly is the effective flag (viewer + handoff, canFill wins),
  // computed once in ValidationStep and threaded through the hook.
  const { entries, hasRun, isRunning } = state;
  const [filter, setFilter] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Two panes on a desktop, two screens on a phone. The effect below still
  // lands the selection on the first thing that needs attention — that
  // decides WHICH entry opens, not whether the detail is on screen.
  const isMobile = useIsMobile();
  const [detailOpen, setDetailOpen] = useState(false);

  const listItems: ValidationListItem[] = useMemo(
    () =>
      entries.map((entry) => ({
        key: entry.key,
        name: entry.name,
        partNumber: entry.partNumber,
        mpn: entry.mpn,
        kind: entry.kind,
        toFix: entry.findings.filter((f) => state.findingStatus(f) === 'needs_review').length,
        hasCritical: entry.findings.some(
          (f) => state.findingStatus(f) === 'needs_review' && severityLabel(f.result) === 'critical',
        ),
        ideas: entry.improvements.filter((i) => state.improvementStatus(i) === 'needs_review').length,
        hadFindings: entry.findings.length > 0,
      })),
    // findingStatus/improvementStatus are stable per render of the hook state.
    [entries, state],
  );

  // Land on the first thing that needs attention; fall back to the BOM-wide
  // entry (Robbie's read + passed checks). Re-anchor if a re-run removed the
  // selected entry.
  useEffect(() => {
    if (selectedKey && entries.some((e) => e.key === selectedKey)) return;
    const firstToFix = listItems.find((it) => it.toFix > 0) || listItems.find((it) => it.ideas > 0);
    setSelectedKey(firstToFix ? firstToFix.key : BOM_ENTRY_KEY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  const query = filter.trim().toLowerCase();
  const visibleItems = query
    ? listItems.filter(
        (it) => it.name.toLowerCase().includes(query) || it.partNumber?.toLowerCase().includes(query) ||
          it.mpn?.toLowerCase().includes(query),
      )
    : listItems;

  const selectedEntry = entries.find((e) => e.key === selectedKey) || null;
  const componentCount = entries.filter((e) => e.kind === 'component').length;
  const longWait = useLongWait(isRunning, validationCopy.longAfterMs(componentCount), state.runStartedAt);
  const suggestions = listItems.reduce((n, it) => n + it.toFix + it.ideas, 0);

  return (
    <div className="relative flex flex-col gap-s5 font-sans">
      <div className="flex flex-wrap items-center gap-s3">
        <SubStepPills items={PILLS} onSelect={onSubStepSelect} />
        {hasRun && !isRunning && (
          <div className="ml-auto flex flex-wrap items-center gap-s3">
            <span className="text-meta text-ink-muted">
              Robbie checked {state.checkedCount} {state.checkedCount === 1 ? 'thing' : 'things'} ·{' '}
              <strong className="text-success-ink">{state.passResults.length} look good</strong> ·{' '}
              <strong className={state.unresolvedCount > 0 ? 'text-warn-ink' : 'text-success-ink'}>
                {state.unresolvedCount} to fix
              </strong>
            </span>
            {state.changesSinceCheck > 0 && (
              <span className="rounded-pill border border-robbie-border bg-ai-chip px-[11px] py-[3px] text-meta font-semibold text-robbie">
                {state.changesSinceCheck} {state.changesSinceCheck === 1 ? 'change' : 'changes'} since last check
              </span>
            )}
            {!state.readOnly && (
              <button
                type="button"
                onClick={state.run}
                className="inline-flex shrink-0 items-center gap-s1 text-meta font-semibold text-neutral-ink hover:text-ink"
              >
                <RefreshCw className="h-[11px] w-[11px]" />
                Ask Robbie to re-check
              </button>
            )}
          </div>
        )}
      </div>

      {handoff && <EndUserHandoffBanner handoff={handoff} />}

      {isRunning || (state.error && !hasRun) ? (
        <QuestionsSkeleton />
      ) : hasRun ? (
        <div
          className={
            isMobile
              ? 'flex flex-col overflow-hidden rounded-card border border-line bg-surface'
              : 'grid h-[calc(100vh-330px)] min-h-[440px] grid-cols-[minmax(150px,220px)_minmax(0,1fr)] overflow-hidden rounded-card border border-line bg-surface'
          }
        >
          {isMobile && detailOpen && (
            <BackToListRow label="All checks" count={visibleItems.length} onBack={() => setDetailOpen(false)} />
          )}
          {(!isMobile || !detailOpen) && (
            <ValidationListPane
              items={visibleItems}
              totalComponents={componentCount}
              selectedKey={selectedKey}
              filter={filter}
              onFilterChange={setFilter}
              onSelect={(key) => {
                setSelectedKey(key);
                setDetailOpen(true);
              }}
            />
          )}
          {isMobile && !detailOpen ? null : selectedEntry ? (
            <ValidationDetailPane
              entry={selectedEntry}
              state={state}
              aiSuggestion={aiSuggestion}
              passedNames={state.passResults.map((r) => formatRuleName(r.ruleName))}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-s3 text-ink-muted">
              <p className="m-0 text-body-md">Select a component to review its checks</p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-s4 rounded-card border border-line bg-surface px-s6 py-[40px] text-center">
          <RobbieMark size="lg" aria-label="Robbie" />
          <p className="m-0 max-w-[420px] text-body-sm text-ink-secondary">
            {state.readOnly
              ? handoff?.isEndUser
                ? "Robbie hasn't checked this BOM yet — checks will appear here once they run."
                : "I haven't checked this BOM yet — the checks are the owner's to run."
              : "I haven't checked this BOM yet — run my checks and I'll flag anything that would make suppliers quote wrong."}
          </p>
          {!state.readOnly && (
            <Button onClick={state.run} iconLeft={<RefreshCw />}>
              Run Robbie's checks
            </Button>
          )}
        </div>
      )}

      <ValidationStepBar
        state={state}
        components={componentCount}
        suggestions={suggestions}
        onBack={onBack}
        onContinue={onContinue}
        isContinueLoading={isContinueLoading}
        handoff={handoff}
      />

      <ValidationOverlay state={state} components={componentCount} long={longWait} onContinue={onContinue} />
    </div>
  );
};

export default ValidationStepPanel;
